import moment from "moment";
import {DomainAncestor, entityConfig} from "core/common/domain/DomainAncestor";
import {observable} from "mobx";
import {EntityConfig, ID} from "core/metamodel/metaConstant";
import {LovItem} from "core/metamodel/mdEntity";
import {blue500, green500, grey500, red500, yellow500} from "material-ui/styles/colors";
import {lovValue} from "core/form/formUtils";
import {Party, PARTYCATEGORY} from "party/partyDomain";
import {ClientGrid} from "core/grid/domain/grid";
import {formatDateTime} from "core/common/utils/dateUtils";




export enum SHIPMENTDIRECTION {
  OUTGOING = "OUTGOING",
  INCOMING = "INCOMING",
}

export enum SHIPMENTSTATUS {
  NOTREADY = "NOTREADY",
  PARTIALLYREADY = "PARTIALLYREADY",
  READY = "READY",
  DOCUMENT = "DOCUMENT"
}

export const SHIPMENTSTATUS_COLORS = {
  NOTREADY: red500,
  PARTIALLYREADY:  yellow500,
  READY: green500,
  DOCUMENT: blue500
};

export const SHIPMENTSTATUS_GRID_CSS = {
  NOTREADY: "fa fa-circle color-red-500",
  PARTIALLYREADY:  "fa fa-circle color-yellow-500",
  READY: "fa fa-circle color-green-500",
  DOCUMENT: "fa fa-circle color-blue-500"
};


export enum SHIPMENTREPACKAGINGSTATUS {
  REPACKAGE = "REPACKAGE",
  PARTIALLYCOMPLETED = "PARTIALLYCOMPLETED",
  COMPLETED = "COMPLETED"
}

export const SHIPMENTREPACKAGINGSTATUS_COLORS = {
  REPACKAGENEXTDAY: grey500,
  REPACKAGE:  red500,
  PARTIALLYCOMPLETED: yellow500,
  COMPLETED: green500
};

export enum SHIPMENTVISITSTEP {
  PREPARED_VISIT = "PREPARED_VISIT",
  VEHICLE_ARRIVED = "VEHICLE_ARRIVED",
  VEHICLE_ENTERED = "VEHICLE_ENTERED",
  VEHICLE_LEFT = "VEHICLE_LEFT"
}



@entityConfig(class extends EntityConfig {
  constructor() {
    super("Shipment");
  }

  getFormName(historyObject) {
    return (historyObject.fullObject.shipmentDirection === SHIPMENTDIRECTION.OUTGOING) ? "ShipmentOutgoingForm" : "ShipmentIncomingForm";
  }
}, "shipmentDetail")
export class Shipment extends DomainAncestor {

  @observable shipmentId: number;
  @observable shipmentDate: string|Date;
  @observable shipmentStatus: LovItem<SHIPMENTSTATUS>;
  @observable shipmentDirection: SHIPMENTDIRECTION;
  @observable shippedFrom: LovItem;
  @observable shippedTo: LovItem;
  @observable hauler: LovItem;
  @observable shippedToAddress: LovItem;
  @observable shippedFromPlaceDesc: string;
  @observable shippedFromPlace: LovItem;
  @observable shippedToPlaceDesc: string;
  @observable shippedToPlace: LovItem;
  @observable registrationNumber: string;
  @observable plannedPaletteCount: number;
  @observable loadedPaletteCount: number;
  @observable notLoadedPaletteCount: number;
  @observable unloadedPaletteCount: number;
  @observable notUnloadedPaletteCount: number;
  @observable loadingNumber: string;
  @observable unloadingNumber: string;
  @observable loadingTruckCount: number;
  @observable unloadingTruckCount: number;
  @observable comment: string;
  @observable pickingList: string;
  @observable shipmentVisit: LovItem;
  @observable transportationType: LovItem;
  @observable maxWaitingTime: number;
  @observable disponentParty: LovItem;
  @observable vehicle: LovItem;
  @observable requestedLoadingStart: string|Date;
  @observable requestedLoadingEnd: string|Date;
  @observable requestedUnloadingStart: string|Date;
  @observable requestedUnloadingEnd: string|Date;
  @observable visitOrder: number;

  @observable $shipmentVisit: ShipmentVisit;

  constructor(json: any) {
    super(json);
  }


  public getId(): ID {
    return this.shipmentId;
  }

  public setId(id: ID): void {
    this.shipmentId = <number>id;
  }

}



@entityConfig()
export class ShipmentRepackaging extends DomainAncestor {

  @observable shipmentRepackagingId: number;
  @observable shipmentDate: string|Date;
  @observable shipmentTime: string|Date;
  @observable repackagingStatus: LovItem<SHIPMENTREPACKAGINGSTATUS>;
  @observable shippedTo: LovItem;
  @observable packageType: LovItem;
  @observable repackagingCode: string;
  @observable boxQuantity: number;
  @observable customerNumber: string;
  @observable paletteCount: number;
  @observable paletteItemCount: number;
  @observable comment: string;
  @observable priority: number;
  @observable product: LovItem;
  @observable shippedToAddress: LovItem;
  @observable repackedDate: string|Date;
  @observable shipmentVisit: LovItem;


  constructor(json: any) {
    super(json);
  }


  public getId(): ID {
    return this.shipmentRepackagingId;
  }

  public setId(id: ID): void {
    this.shipmentRepackagingId = <number>id;
  }

  public getRepackagingStatusIconClass(keDni, status: SHIPMENTREPACKAGINGSTATUS) :String {
    if (lovValue(status) == SHIPMENTREPACKAGINGSTATUS.REPACKAGE) {
      return ((!this.shipmentDate || moment(this.shipmentDate).isAfter(keDni)) ? grey500 : red500);
    } else {
      return SHIPMENTREPACKAGINGSTATUS_COLORS[lovValue(status)];
    }
  }

}



@entityConfig()
export class ShipmentRepackagingResource extends DomainAncestor {

  @observable workDate: string;
  @observable availableResources: number;
  @observable dailyPalleteNorm: number;
  @observable completedThisDay: number;
  @observable toRepackThisDay: number;


  constructor(json: any) {
    super(json);
  }

  public getId(): ID {
    return this.workDate;
  }

  public setId(id: ID): void {
    this.workDate = <string>id;
  }

  public getDailyNorm() : number {
    return Math.ceil(this.availableResources * this.dailyPalleteNorm);
  }

}


@entityConfig()
export class ShipmentVisit extends DomainAncestor {

  @observable shipmentVisitId: number;
  @observable visitTime: string|Date;
  @observable visitEnd: string|Date;
  @observable entranceTime: string|Date;
  @observable gateLoadingDuration: string;
  @observable vendorLoadingDuration: string;

  @observable registrationNumber: string;
  @observable driverPhone: string;
  @observable directionOutgoing: boolean;
  @observable directionPackaging: boolean;  // originally part of directionOutgoing
  @observable directionIncoming: boolean;
  @observable packagingSender: LovItem<ID>;
  @observable step: LovItem<SHIPMENTVISITSTEP>;

  @observable shipments: Shipment[];
  @observable $shipmentVisitShipmentsGrid: ClientGrid;
  @observable $shipmentVisitSuppliers: LovItem<ID>[];
  @observable $supplierId: LovItem<number>;
  @observable $syncRegistrationNumber: boolean;
  @observable $syncShipmentStatus: SHIPMENTSTATUS;
  @observable $syncShippedFromPlace: LovItem<number>;
  @observable $direction: LovItem<SHIPMENTDIRECTION>;
  @observable $nextTime: string|Date;
  @observable $showSmsToDriver: boolean;
  $previousShipments: Shipment[];
  @observable $visitsToDelete: ShipmentVisit[];


  constructor(json: any) {
    super(json);
  }

  public getId(): ID {
    return this.shipmentVisitId;
  }

  public setId(id: ID): void {
    this.shipmentVisitId = <number>id;
  }

  public shipmentVisitLabel(): string {
    return [this.registrationNumber, this.driverPhone].join(" ") + " - " + formatDateTime(this.visitTime);
  }

  shipmentVisitLov() {
    return {
      value: this.shipmentVisitId,
      label: this.shipmentVisitLabel()
    };
  }


}



// WEBPACK FOOTER //
// ./src/shipment/shipmentDomain.ts